import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { trackUserAction } from "@/utils/tracking";
import { LucideIcon } from "lucide-react";

interface LegalSectionProps {
  id: string;
  title: string;
  icon: LucideIcon;
  status: string;
  content: string[];
  documentPath?: string;
}

export const LegalSection = ({
  id,
  title,
  icon: Icon,
  status,
  content,
  documentPath,
}: LegalSectionProps) => {
  const handleDownload = async () => {
    if (!documentPath) return;
    await trackUserAction('download', { file_name: `${title.toLowerCase()}_document` });
    window.open(documentPath, '_blank');
  };

  return (
    <Accordion type="single" collapsible>
      <AccordionItem value={id} className="border-none">
        <AccordionTrigger className="px-4 py-2 hover:no-underline">
          <div className="flex items-center gap-4 w-full">
            <div className="p-2 rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors">
              <Icon className="h-5 w-5" />
            </div>
            <div className="flex flex-col items-start">
              <h3 className="font-semibold text-lg">{title}</h3>
              <Badge 
                variant="secondary"
                className="mt-1 group-hover:bg-primary/20"
              >
                {status}
              </Badge>
            </div>
            {documentPath && (
              <Button
                variant="outline"
                size="sm"
                className="ml-auto flex items-center gap-2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
              >
                <Download className="h-4 w-4" />
                Download
              </Button>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <div className="pl-14 space-y-2 animate-fade-in">
            {content.map((item, i) => (
              <div 
                key={i}
                className="flex items-center gap-2 text-muted-foreground"
                style={{
                  animationDelay: `${i * 100}ms`,
                }}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};